import type { EditorHandle } from "@shared/ui/Editor/ui";
import {
  DOCUMENT_ACCEPT,
  collectFilesFromDataTransfer,
  isDocumentFile,
  isVideoFile,
} from "./editorFileTypes";
import {
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  type RefObject,
} from "react";

type ShowStatus = (message: string, tone?: "ok" | "err") => void;

type UseEditorDocumentImportArgs = {
  editorRef: RefObject<EditorHandle | null>;
  disabled: boolean;
  showStatus: ShowStatus;
  onContentChange?: () => void;
  onImportComplete?: () => void;
};

export function useEditorDocumentImport({
  editorRef,
  disabled,
  showStatus,
  onContentChange,
  onImportComplete,
}: UseEditorDocumentImportArgs) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const importDocument = async (file: File) => {
    if (disabled || isImporting) return;
    if (!editorRef.current) {
      showStatus("Editor is not ready yet", "err");
      return;
    }
    if (isVideoFile(file)) {
      showStatus("Videos were skipped");
      return;
    }
    if (!isDocumentFile(file)) {
      showStatus("Upload a .docx or .pdf file", "err");
      return;
    }

    setIsImporting(true);
    try {
      await editorRef.current.importFile(file);
      onImportComplete?.();
      showStatus(`Imported “${file.name}”`);
    } catch (error) {
      showStatus(
        error instanceof Error ? error.message : "Could not import file",
        "err"
      );
    } finally {
      setIsImporting(false);
      onContentChange?.();
    }
  };

  const onFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) void importDocument(file);
  };

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    const files = collectFilesFromDataTransfer(event.dataTransfer);
    if (!files.some(isDocumentFile)) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
    if (!disabled && !isImporting) setIsDragOver(true);
  };

  const onDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return;
    }
    setIsDragOver(false);
  };

  const consumeDocumentOrVideo = (
    event: DragEvent<HTMLDivElement> | ClipboardEvent<HTMLDivElement>,
    files: File[]
  ) => {
    const documentFile = files.find(isDocumentFile);
    if (documentFile) {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);
      void importDocument(documentFile);
      return true;
    }
    if (files.length > 0 && files.every(isVideoFile)) {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);
      showStatus("Videos were skipped");
      return true;
    }
    return false;
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    consumeDocumentOrVideo(
      event,
      collectFilesFromDataTransfer(event.dataTransfer)
    );
  };

  const onPaste = (event: ClipboardEvent<HTMLDivElement>) => {
    consumeDocumentOrVideo(
      event,
      collectFilesFromDataTransfer(event.clipboardData)
    );
  };

  return {
    fileInputRef,
    accept: DOCUMENT_ACCEPT,
    isImporting,
    isDragOver,
    openFilePicker: () => fileInputRef.current?.click(),
    onFileInputChange,
    onDragOver,
    onDragLeave,
    onDrop,
    onPaste,
  };
}
